import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, Lock, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../lib/auth';
import { siteConfig } from '../../config/site';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

export default function PortalPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/emr/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '64px 64px'
          }}
        />

        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <Link to="/" className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div>
              <span className="text-3xl font-bold text-white">{siteConfig.name.split(' ').slice(0, 2).join(' ')}</span>
              <span className="block text-sm text-slate-400">{siteConfig.name.split(' ').slice(2).join(' ') || 'Group'}</span>
            </div>
          </Link>

          <div className="max-w-md text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Electronic Medical Records
            </h1>
            <p className="text-slate-400 text-lg">
              Secure access to patient records, scheduling, forms, and clinical documentation 
              for authorized healthcare providers.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-emerald-400">HIPAA</div>
              <div className="text-slate-500 text-sm">Compliant</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">256-bit</div>
              <div className="text-slate-500 text-sm">Encryption</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">24/7</div>
              <div className="text-slate-500 text-sm">Access</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="w-full max-w-md"
        >
          {/* Back to website link */}
          <motion.div variants={fadeInUp} className="mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
              data-testid="back-to-site"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to website
            </Link>
          </motion.div>

          {/* Mobile logo */}
          <motion.div variants={fadeInUp} className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-white">Apex Medical</span>
              <span className="block text-xs text-slate-400">Group</span>
            </div>
          </motion.div>

          {/* Form header */}
          <motion.div variants={fadeInUp} className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Doctor Portal</h2>
            <p className="text-slate-400">
              Sign in to access the EMR system
            </p>
          </motion.div>

          {/* Login form */}
          <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-white">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder="doctor@apexmedical.lc"
                  data-testid="portal-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white">Password</Label>
                <button 
                  type="button"
                  className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
                  data-testid="forgot-password"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="pl-10 pr-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder="••••••••"
                  data-testid="portal-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/20"
              disabled={loading}
              data-testid="portal-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </motion.form>

          {/* Security notice */}
          <motion.div variants={fadeInUp} className="mt-8 p-4 rounded-xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-400">
                <p className="font-medium text-slate-300 mb-1">Secure Access</p>
                <p>
                  This portal contains protected health information. Unauthorized access 
                  is prohibited and may be subject to civil and criminal penalties.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Admin access note */}
          <motion.p variants={fadeInUp} className="mt-6 text-center text-slate-500 text-sm">
            Need access? Contact your system administrator.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
